import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const PortfolioChart = ({ tokenId, assetOverview, assetsLoading }) => {
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [error, setError] = useState(null);


  const periods = [
    { value: '7d', label: '7D' },
    { value: '30d', label: '1M' },
    { value: '90d', label: '3M' },
    { value: '1y', label: '1Y' }
  ];

  // Calculate summary based on actual historical data or weighted average of asset price changes
  const calculateSummaryFromAssets = async (assets, totalValueUsd) => {
    // Use pre-calculated total from backend instead of calculating from assets
    const totalValue = parseFloat(totalValueUsd) || 0;

    if (!assets || assets.length === 0 || totalValue === 0) {
      return {
        '24h': { changePercent: 0, changeValue: 0 },
        '1w': { changePercent: 0, changeValue: 0 },
        '1m': { changePercent: 0, changeValue: 0 }
      };
    }
    
    try {
      // Try to fetch actual historical data
      const historicalResponse = await api.get(`/asset-historical/${tokenId}`);
      const historical = historicalResponse.data;
      
      if (historical && historical.history) {
        const value24hAgo = historical.history['24h'] || totalValue;
        const value1wAgo = historical.history['1w'] || totalValue;
        const value1mAgo = historical.history['1m'] || totalValue;
        
        // Calculate percentage changes based on historical values
        const change24h = value24hAgo > 0 ? ((totalValue - value24hAgo) / value24hAgo) * 100 : 0;
        const change1w = value1wAgo > 0 ? ((totalValue - value1wAgo) / value1wAgo) * 100 : 0;
        const change1m = value1mAgo > 0 ? ((totalValue - value1mAgo) / value1mAgo) * 100 : 0;
        
        return {
          '24h': { 
            changePercent: parseFloat(change24h.toFixed(2)), 
            changeValue: parseFloat((totalValue - value24hAgo).toFixed(2)) 
          },
          '1w': { 
            changePercent: parseFloat(change1w.toFixed(2)), 
            changeValue: parseFloat((totalValue - value1wAgo).toFixed(2))
          },
          '1m': { 
            changePercent: parseFloat(change1m.toFixed(2)), 
            changeValue: parseFloat((totalValue - value1mAgo).toFixed(2))
          }
        };
      }
    } catch (error) {
      console.error('Error fetching historical data, falling back to weighted average:', error);
    }
    
    // Fallback to weighted average calculation
    let weighted24hChange = 0;
    assets.forEach(asset => {
      const weight = (parseFloat(asset.value) || 0) / totalValue;
      const change = parseFloat(asset.change24h) || 0;
      weighted24hChange += change * weight;
    });
    
    const change24h = weighted24hChange;
    const changeValue24h = (totalValue * change24h) / 100;
    
    // For 1w and 1m, estimate based on 24h change (this is a fallback)
    const change1w = change24h * 3.5; // Rough estimate
    const change1m = change24h * 15; // Rough estimate
    
    return {
      '24h': { 
        changePercent: parseFloat(change24h.toFixed(2)), 
        changeValue: parseFloat(changeValue24h.toFixed(2)) 
      },
      '1w': { 
        changePercent: parseFloat(change1w.toFixed(2)), 
        changeValue: parseFloat((totalValue * change1w / 100).toFixed(2))
      },
      '1m': { 
        changePercent: parseFloat(change1m.toFixed(2)), 
        changeValue: parseFloat((totalValue * change1m / 100).toFixed(2))
      }
    };
  };

  useEffect(() => {
    if (tokenId) {
      fetchPortfolioData();
      fetchPortfolioSummary();
    }
  }, [tokenId, selectedPeriod]);
  
  // Recalculate summary when asset overview changes
  useEffect(() => {
    const updateSummary = async () => {
      if (assetOverview?.assets) {
        setSummaryLoading(true);
        const calculatedSummary = await calculateSummaryFromAssets(assetOverview.assets, assetOverview.totalValueUsd);
        setSummary(calculatedSummary);
        setSummaryLoading(false);
      } else if (assetOverview !== null && assetOverview !== undefined) {
        // assetOverview loaded but no assets - set to zero and stop loading
        setSummary({
          '24h': { changePercent: 0, changeValue: 0 },
          '1w': { changePercent: 0, changeValue: 0 },
          '1m': { changePercent: 0, changeValue: 0 }
        });
        setSummaryLoading(false);
      }
    };
    updateSummary();
  }, [assetOverview]);

  const fetchPortfolioData = useCallback(async () => {
    try {
      const response = await api.get(`/portfolio-history/${tokenId}?period=${selectedPeriod}`);

      // Check if we have valid data
      if (!response.data || !response.data.data || !Array.isArray(response.data.data)) {
        console.error('Invalid portfolio data structure:', response.data);
        setError('Invalid data format received');
        setChartData([]);
        return;
      }

      // Format data for recharts
      const formattedData = response.data.data
        .map(point => ({
          date: point.date,
          value: parseFloat(point.value) || 0,
          changePercent: parseFloat(point.changePercent) || 0,
          formattedDate: formatDate(point.date),
          solBalance: parseFloat(point.solBalance) || 0,
          tokensValue: parseFloat(point.tokensValue) || 0
        }))
        .filter(point => !isNaN(point.value)); // Filter out invalid data points

      console.log('📊 Chart data details:', {
        dataLength: formattedData.length,
        sampleData: formattedData.slice(0, 3),
        allValues: formattedData.map(d => ({ date: d.date, value: d.value })),
        hasValidData: formattedData.length > 0 && formattedData.some(d => d.value > 0)
      });

      if (formattedData.length === 0) {
        console.warn('No valid data points after formatting');
        setError('No valid data to display');
        setChartData([]);
        return;
      }

      setChartData(formattedData);
      setError(null);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      console.error('Error details:', error.response?.data || error.message);
      setError(`Failed to load portfolio data: ${error.response?.data?.message || error.message}`);
      setChartData([]);
    }
  }, [tokenId, selectedPeriod]);

  const fetchPortfolioSummary = async () => {
    try {
      setSummaryLoading(true);
      // If we have asset overview, calculate summary from asset price changes
      if (assetOverview?.assets) {
        const calculatedSummary = await calculateSummaryFromAssets(assetOverview.assets, assetOverview.totalValueUsd);
        setSummary(calculatedSummary);
      } else {
        const response = await api.get(`/portfolio-summary/${tokenId}`);
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching portfolio summary:', error);
      // Fallback to calculated summary from assets or default
      if (assetOverview?.assets) {
        const calculatedSummary = await calculateSummaryFromAssets(assetOverview.assets, assetOverview.totalValueUsd);
        setSummary(calculatedSummary);
      } else {
        setSummary({
          '24h': { changePercent: 0, changeValue: 0 },
          '1w': { changePercent: 0, changeValue: 0 },
          '1m': { changePercent: 0, changeValue: 0 }
        });
      }
    } finally {
      setSummaryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (selectedPeriod === '7d') {
      return date.toLocaleDateString(undefined, { weekday: 'short' });
    } else if (selectedPeriod === '30d') {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } else if (selectedPeriod === '90d') {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    }
  };



  const getChangeColor = (changePercent) => {
    if (changePercent > 0) return 'text-green-600';
    if (changePercent < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const getChangeIcon = (changePercent) => {
    if (changePercent > 0) return <TrendingUp className="w-4 h-4" />;
    if (changePercent < 0) return <TrendingDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  // Use pre-calculated total from backend (will be 0 for new users with no assets)
  const calculatedTotal = parseFloat(assetOverview?.totalValueUsd) || 0;
  
  // Always prioritize asset overview data, never show historical inflated values
  const currentValue = calculatedTotal;
    
  const overallChange = chartData.length > 0 ? chartData[chartData.length - 1]?.changePercent : 0;
  // When there's insufficient data (less than 2 points), default to neutral/positive appearance
  const isPositive = chartData.length < 2 ? true : overallChange > 0;

  // Show loading if explicitly told assets are loading OR if asset overview is null/undefined (not loaded yet)
  // If assetOverview.assets is an empty array AND assetsLoading is false, that means it loaded but user has no assets
  const isReallyLoading = assetsLoading || !assetOverview;


  if (isReallyLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }
  
  // Show empty state for users with no assets (only when not loading)
  const hasNoAssets = !assetsLoading && assetOverview?.assets && assetOverview.assets.length === 0;

  if (hasNoAssets) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Portfolio Value</h3>
          <TrendingUp className="w-5 h-5 text-gray-400" />
        </div>
        
        <div className="text-3xl font-bold mb-4 text-gray-900 dark:text-gray-100">
          $0.00
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-xs text-gray-500">24h</div>
            <div className="text-sm font-semibold text-gray-500">0.0%</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">1w</div>
            <div className="text-sm font-semibold text-gray-500">0.0%</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">1mo</div>
            <div className="text-sm font-semibold text-gray-500">0.0%</div>
          </div>
        </div>

        <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <div className="text-center">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 font-medium">No trading activity yet</p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Start trading to see your portfolio performance</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Portfolio Value</h3>
        {assetsLoading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-500"></div>
        ) : (
          <TrendingUp className="w-5 h-5 text-gray-400" />
        )}
      </div>
      
      {/* Current Value Display */}
      {assetsLoading ? (
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
            <span className="text-2xl font-bold text-gray-400">Loading portfolio value...</span>
          </div>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
          </div>
        </div>
      ) : (
        <div className="text-3xl font-bold mb-4">
          ${currentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}

      {/* Performance Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-xs text-gray-500">24h</div>
          {summaryLoading ? (
            <div className="flex items-center justify-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-purple-500"></div>
              <span className="text-xs text-gray-400">Loading...</span>
            </div>
          ) : (
            <div className={`text-sm font-semibold flex items-center justify-center ${getChangeColor(summary['24h']?.changePercent || 0)}`}>
              {getChangeIcon(summary['24h']?.changePercent || 0)}
              {summary['24h']?.changePercent > 0 ? '+' : ''}{(summary['24h']?.changePercent || 0).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-500">1w</div>
          {summaryLoading ? (
            <div className="flex items-center justify-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-purple-500"></div>
              <span className="text-xs text-gray-400">Loading...</span>
            </div>
          ) : (
            <div className={`text-sm font-semibold flex items-center justify-center ${getChangeColor(summary['1w']?.changePercent || 0)}`}>
              {getChangeIcon(summary['1w']?.changePercent || 0)}
              {summary['1w']?.changePercent > 0 ? '+' : ''}{(summary['1w']?.changePercent || 0).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-500">1mo</div>
          {summaryLoading ? (
            <div className="flex items-center justify-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-purple-500"></div>
              <span className="text-xs text-gray-400">Loading...</span>
            </div>
          ) : (
            <div className={`text-sm font-semibold flex items-center justify-center ${getChangeColor(summary['1m']?.changePercent || 0)}`}>
              {getChangeIcon(summary['1m']?.changePercent || 0)}
              {summary['1m']?.changePercent > 0 ? '+' : ''}{(summary['1m']?.changePercent || 0).toFixed(1)}%
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="relative" style={{ height: '256px', minHeight: '256px' }}>
        {error ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="font-medium">Unable to load chart data</p>
              <p className="text-xs mt-1 text-red-500">{error}</p>
              <button
                onClick={() => fetchPortfolioData()}
                className="mt-3 px-3 py-1 text-xs bg-purple-100 text-purple-600 rounded hover:bg-purple-200"
              >
                Retry
              </button>
            </div>
          </div>
        ) : !chartData || chartData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto mb-2"></div>
              <p>Loading chart data...</p>
            </div>
          </div>
        ) : chartData.length > 0 ? (
          <div style={{ position: 'relative', width: '100%', height: '240px' }}>
            <Line
              data={{
                labels: chartData.map(d => d.formattedDate),
                datasets: [{
                  label: 'Portfolio Value',
                  data: chartData.map(d => d.value),
                  borderColor: isPositive ? '#10B981' : '#EF4444',
                  backgroundColor: isPositive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderWidth: 2,
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  pointBackgroundColor: isPositive ? '#10B981' : '#EF4444',
                  pointBorderColor: '#fff',
                  pointBorderWidth: 2
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#111827',
                    bodyColor: '#6B7280',
                    borderColor: '#E5E7EB',
                    borderWidth: 1,
                    padding: 12,
                    displayColors: false,
                    callbacks: {
                      label: (context) => {
                        return `$${context.parsed.y.toFixed(2)}`;
                      }
                    }
                  }
                },
                interaction: {
                  mode: 'nearest',
                  axis: 'x',
                  intersect: false
                },
                scales: {
                  x: {
                    grid: {
                      display: false
                    },
                    ticks: {
                      color: '#6B7280',
                      font: {
                        size: 10
                      }
                    }
                  },
                  y: {
                    grid: {
                      color: '#E5E7EB',
                      drawBorder: false
                    },
                    ticks: {
                      color: '#6B7280',
                      font: {
                        size: 10
                      },
                      callback: (value) => `$${Math.round(value)}`
                    }
                  }
                }
              }}
            />
          </div>
        ) : null}
      </div>

      {/* Chart Period Label */}
      <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
        <span>
          {selectedPeriod === '7d' ? '7 days ago' : 
           selectedPeriod === '30d' ? '30 days ago' : 
           selectedPeriod === '90d' ? '3 months ago' : '1 year ago'}
        </span>
        <span>Now</span>
      </div>

      {/* Period Selector */}
      <div className="flex justify-center mt-4">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {periods.map((period) => (
            <button
              key={period.value}
              onClick={() => setSelectedPeriod(period.value)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === period.value
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortfolioChart;